/**
 * Soft "thmp" UI select/click cue — a tiny WebAudio synth (no asset).
 *
 * A low sine (~92 Hz) with a very fast attack and an exponential decay,
 * gated through a gentle lowpass so there is no harsh transient. Total
 * duration ~70ms at a low peak gain, so it reads as a soft muted tap
 * rather than a click or beep.
 *
 * The AudioContext is created lazily on the first call (a user gesture),
 * satisfying browser autoplay policy, and is reused thereafter. All
 * failures are swallowed so a UI cue never throws.
 */

export type ThmpOptions = {
  /** Peak gain override (soft by default). Clamped to [0, 0.5]. */
  volume?: number;
  /** When true, skip playback (reduced-motion / muted preference). */
  muted?: boolean;
};

type WindowWithWebkitAudio = Window &
  typeof globalThis & {
    webkitAudioContext?: typeof AudioContext;
  };

// Envelope parameters — soft, short, exponential decay (see module doc).
const THMP_FREQ_HZ = 92; // low sine, feels like a muted tap
const THMP_ATTACK_S = 0.004; // 4ms fade-in — fast but not an instant edge (no pop)
const THMP_DURATION_S = 0.07; // ~70ms total
const THMP_PEAK_GAIN = 0.14; // soft
const THMP_LOWPASS_HZ = 320; // tame any harsh transient
const THMP_RELEASE_S = 0.012; // final fade to true zero so the tail never clips

let ctx: AudioContext | null = null;

function resolveAudioContext(): AudioContext | null {
  if (ctx) return ctx;
  if (typeof window === "undefined") return null;
  const w = window as WindowWithWebkitAudio;
  const Ctor = w.AudioContext ?? w.webkitAudioContext;
  if (!Ctor) return null;
  try {
    ctx = new Ctor();
  } catch {
    return null;
  }
  return ctx;
}

/**
 * Play one soft low thump. Never rejects — autoplay/context errors are caught.
 * Safe to call on every UI select; each call is an independent one-shot.
 */
export function playThmp(opts: ThmpOptions = {}): void {
  if (opts.muted) return;
  const audio = resolveAudioContext();
  if (!audio) return;

  try {
    // Some browsers start the context suspended until a gesture resumes it.
    if (audio.state === "suspended") {
      void audio.resume().catch(() => {
        /* ignore */
      });
    }

    const peak = Math.min(0.5, Math.max(0, opts.volume ?? THMP_PEAK_GAIN));
    if (peak <= 0) return;

    const now = audio.currentTime;
    const osc = audio.createOscillator();
    const gain = audio.createGain();
    const lp = audio.createBiquadFilter();

    osc.type = "sine";
    osc.frequency.setValueAtTime(THMP_FREQ_HZ, now);

    lp.type = "lowpass";
    lp.frequency.setValueAtTime(THMP_LOWPASS_HZ, now);

    // Smooth envelope: linear attack, exponential body decay, short linear
    // release to zero. exponentialRampToValueAtTime cannot reach 0, so we
    // decay toward a tiny floor then ramp linearly to silence.
    const floor = 0.0001;
    gain.gain.setValueAtTime(floor, now);
    gain.gain.linearRampToValueAtTime(peak, now + THMP_ATTACK_S);
    gain.gain.exponentialRampToValueAtTime(
      floor,
      now + THMP_DURATION_S,
    );
    gain.gain.linearRampToValueAtTime(
      0,
      now + THMP_DURATION_S + THMP_RELEASE_S,
    );

    osc.connect(lp);
    lp.connect(gain);
    gain.connect(audio.destination);

    const stopAt = now + THMP_DURATION_S + THMP_RELEASE_S;
    osc.start(now);
    osc.stop(stopAt);
    osc.onended = () => {
      try {
        osc.disconnect();
        lp.disconnect();
        gain.disconnect();
      } catch {
        /* ignore */
      }
    };
  } catch {
    /* autoplay blocked / node creation failed — never throw for a UI cue */
  }
}

/** Release the shared AudioContext (tests / teardown). */
export function disposeThmp(): void {
  if (!ctx) return;
  try {
    void ctx.close();
  } catch {
    /* ignore */
  }
  ctx = null;
}

/** Test-only accessor for the lazily-created context (null until first play). */
export function getThmpContextForTests(): AudioContext | null {
  return ctx;
}
